import Vue from "vue";
import configPOPTheme from "@/config/POPTheme";
import { BANNER_API } from "@/constants/BannerApi";

// IT-3404 — "ประกาศข่าวสาร": archive of SHIPPOP-BANNER campaigns shown on this brand's POS page
// (configPOPTheme.bannerCampaignPosition, e.g. DF_POSPOPSHOP) via BANNER_API (UAT/PROD per build). Plain fetch, like Pos.vue's banner call:
// the app's $http adds the POP auth token, which must not be sent to the ads service.

// POPSHOP only: white-label themes don't set showAnnouncement, so nothing renders or calls the API for them
export const ANNOUNCEMENT_POSITION = (configPOPTheme.showAnnouncement && configPOPTheme.bannerCampaignPosition) || "";

const READ_KEY = "pop_announcement_read"; // ids of announcements the user has seen
const CACHE_KEY = "pop_announcement_active"; // running ids, cached so the API isn't hit on every page
const CACHE_TTL = 10 * 60 * 1000;
const MAX_READ = 300;

// reactive unread count shared by the navbar and the mobile sidebar entry
export const announcementState = Vue.observable({ unread: 0 });

const query = (params) =>
  Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== "")
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
    .join("&");

export const announcementDevice = () => (window.innerWidth < 576 ? "mobile" : "desktop");

// GET /campaign/history for this brand's POS position. Resolves { metadata, result }.
export const getAnnouncements = (params) =>
  fetch(
    BANNER_API +
      "/campaign/history?" +
      query({ position_page: ANNOUNCEMENT_POSITION, device: announcementDevice(), ...params })
  ).then((res) => {
    if (!res.ok) throw new Error("HTTP " + res.status);
    return res.json();
  });

export const trackAnnouncementClick = (campaignId, positionId) => {
  if (!positionId) return;
  fetch(BANNER_API + "/click", {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    keepalive: true,
    referrerPolicy: "no-referrer",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ campaign_id: campaignId, position_id: positionId }),
  }).catch(() => {});
};

const readIds = () => {
  try {
    const v = JSON.parse(localStorage.getItem(READ_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};

const cachedActive = () => {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
    return c && Array.isArray(c.ids) && Date.now() - c.at < CACHE_TTL ? c.ids : null;
  } catch (e) {
    return null;
  }
};

const countUnread = (activeIds) => {
  const read = readIds();
  announcementState.unread = (activeIds || []).filter((id) => !read.includes(id)).length;
};

// ids of announcements running now; force=true bypasses both the session cache and the API's HTTP cache
export const fetchActiveIds = (force) => {
  const cached = force ? null : cachedActive();
  if (cached) return Promise.resolve(cached);
  return getAnnouncements({ status: "active", limit: 30, nocache: force ? 1 : undefined })
    .then((res) => {
      const ids = (res.result || []).map((c) => c.id).filter(Boolean);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), ids }));
      } catch (e) {
        // storage unavailable: the count still works for this page view
      }
      return ids;
    })
    .catch(() => []);
};

export const refreshUnread = () => {
  if (!ANNOUNCEMENT_POSITION) return Promise.resolve();
  return fetchActiveIds(false).then(countUnread);
};

// opening the announcements page reads every running announcement
export const markAllActiveRead = () =>
  fetchActiveIds(true).then((ids) => {
    const read = readIds();
    ids.forEach((id) => {
      if (!read.includes(id)) read.push(id);
    });
    try {
      localStorage.setItem(READ_KEY, JSON.stringify(read.slice(-MAX_READ)));
    } catch (e) {
      // storage unavailable: nothing to persist
    }
    countUnread(ids);
  });
